/* eslint-disable react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization -- Effects sync local group/loading state with React Query data; sortedBalances pins finer-grained deps (group?.balances/createdBy) than the React Compiler can infer. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type PendingInvite,
  PlaceholderMemberService,
  groupMemberService,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { GroupWithDashboard } from '@splitfiesta/shared';

import {
  useDeletePlaceholderMutation,
  useGroupWithDashboardQuery,
  useRemoveGroupMemberMutation,
  useUpdateMemberRoleMutation,
  useUpdatePlaceholderMutation,
} from '@/hooks/queries/useGroups';
import { useEnhancedError } from '@/hooks/useEnhancedError';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { useAuthStore } from '@/stores/authStore';

import { getErrorMessage, handleError } from '@/utils/errorHandler';

interface UseGroupMembersProps {
  groupId: string;
}

export function useGroupMembers({ groupId }: UseGroupMembersProps) {
  const user = useAuthStore(state => state.user);
  const { t } = useTranslation();

  // Use React Query hooks
  const {
    data: groupData,
    isLoading: groupLoading,
    refetch: refetchGroup,
    // The roster is cached for minutes, and another member's join or claim
    // never invalidates this browser, so the members page always asks again.
  } = useGroupWithDashboardQuery(groupId, !!user, { refetchOnMount: 'always' });
  const removeMemberMutation = useRemoveGroupMemberMutation(groupId);
  const updateMemberRoleMutation = useUpdateMemberRoleMutation(groupId);
  const updatePlaceholderMutation = useUpdatePlaceholderMutation(groupId);
  const deletePlaceholderMutation = useDeletePlaceholderMutation(groupId);

  // Placeholder changes are online-only: nothing is queued or applied
  // optimistically, so the UI disables them rather than failing later.
  const { isOnline } = useNetworkStatus();

  // State
  const [group, setGroup] = useState<GroupWithDashboard | null>(null);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [inviting] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<string | null>(null);
  const [renamingPlaceholder, setRenamingPlaceholder] = useState<string | null>(
    null
  );
  const [removingPlaceholder, setRemovingPlaceholder] = useState<string | null>(
    null
  );
  const [placeholderError, setPlaceholderError] = useState('');
  const [success, setSuccess] = useState<
    { key: string; params?: Record<string, unknown> } | string
  >('');

  // Enhanced error handling
  const { error, setError } = useEnhancedError({
    namespace: 'members',
  });

  // Use a ref to track if initial load has been done
  const initialLoadDone = useRef(false);

  // Update group state when data is fetched
  useEffect(() => {
    if (groupData) {
      setGroup(groupData as GroupWithDashboard);
      // TODO: Implement invites endpoint and fetch pending invites
      setPendingInvites([]);
    }
  }, [groupData]);

  // Update loading state
  useEffect(() => {
    setInitialLoading(groupLoading);
  }, [groupLoading]);

  const fetchMemberData = useCallback(async () => {
    await refetchGroup();
  }, [refetchGroup]);

  useEffect(() => {
    if (!initialLoadDone.current && groupId && user) {
      initialLoadDone.current = true;
      fetchMemberData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, user]);

  const handleRemoveMember = useCallback(
    async (userId: string, userName: string) => {
      const confirmMessage = groupMemberService.formatRemoveConfirmation(
        userName,
        t
      );
      if (!confirm(confirmMessage)) {
        return;
      }

      setRemoving(userId);
      setError('');

      try {
        await removeMemberMutation.mutateAsync(userId);

        setSuccess(groupMemberService.formatMemberRemovedMessage(userName, t));

        // Refresh data
        await fetchMemberData();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: unknown) {
        const errorMessage = groupMemberService.handleMemberError(err, t);
        setError(errorMessage);
      } finally {
        setRemoving(null);
      }
    },
    [removeMemberMutation, t, fetchMemberData, setError]
  );

  const handlePromoteToAdmin = useCallback(
    async (userId: string, userName: string) => {
      const confirmMessage = t('members:messages.confirmPromote', {
        name: userName,
      });
      if (!confirm(confirmMessage)) {
        return;
      }

      setPromoting(userId);
      setError('');

      try {
        await updateMemberRoleMutation.mutateAsync({
          memberId: userId,
          role: 'admin',
        });

        setSuccess({
          key: 'members:messages.memberPromoted',
          params: { name: userName },
        });

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: unknown) {
        const errorMessage = groupMemberService.handleMemberError(err, t);
        setError(errorMessage);
      } finally {
        setPromoting(null);
      }
    },
    [updateMemberRoleMutation, t, setError]
  );

  const handleDemoteToMember = useCallback(
    async (userId: string, userName: string) => {
      const confirmMessage = t('members:messages.confirmDemote', {
        name: userName,
      });
      if (!confirm(confirmMessage)) {
        return;
      }

      setPromoting(userId);
      setError('');

      try {
        await updateMemberRoleMutation.mutateAsync({
          memberId: userId,
          role: 'member',
        });

        setSuccess({
          key: 'members:messages.memberDemoted',
          params: { name: userName },
        });

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: unknown) {
        const errorMessage = groupMemberService.handleMemberError(err, t);
        setError(errorMessage);
      } finally {
        setPromoting(null);
      }
    },
    [updateMemberRoleMutation, t, setError]
  );

  // Resolves true only when the rename was valid AND accepted, so the caller
  // can keep an invalid edit open instead of discarding what was typed.
  const handleRenamePlaceholder = useCallback(
    async (userId: string, name: string): Promise<boolean> => {
      const { valid, trimmed } = PlaceholderMemberService.validateName(name);
      if (!valid) {
        return false;
      }

      setRenamingPlaceholder(userId);
      setPlaceholderError('');

      try {
        await updatePlaceholderMutation.mutateAsync({
          userId,
          name: trimmed,
        });
        return true;
      } catch (err: unknown) {
        setPlaceholderError(getErrorMessage(handleError(err, 'groups'), t));
        return false;
      } finally {
        setRenamingPlaceholder(null);
      }
    },
    [updatePlaceholderMutation, t]
  );

  const handleRemovePlaceholder = useCallback(
    async (userId: string, userName?: string) => {
      const confirmMessage = [
        t('groups:placeholderMembers.deleteConfirm', { name: userName }),
        t('groups:placeholderMembers.deleteConfirmDetail', { name: userName }),
      ].join('\n\n');
      if (!confirm(confirmMessage)) {
        return;
      }

      setRemovingPlaceholder(userId);
      setPlaceholderError('');

      try {
        await deletePlaceholderMutation.mutateAsync(userId);

        setSuccess({ key: 'groups:placeholderMembers.removed' });

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: unknown) {
        setPlaceholderError(getErrorMessage(handleError(err, 'groups'), t));
      } finally {
        setRemovingPlaceholder(null);
      }
    },
    [deletePlaceholderMutation, t]
  );

  // Computed values - use userRole directly now that owner is a distinct role
  const isGroupOwner = group?.userRole === 'owner';

  // Check if current user is an admin (owner or has admin role)
  const isCurrentUserAdmin = useMemo(() => {
    if (!group) return false;
    return group.userRole === 'admin' || group.userRole === 'owner';
  }, [group]);

  // Memoize excludeIds to prevent infinite re-renders in FriendsSelector
  const excludeIds = useMemo(() => {
    return groupMemberService.getMemberExcludeIds(group);
  }, [group]);

  // Sort members: owner first, then current user (if not owner), then others
  const sortedBalances = useMemo(() => {
    if (!group?.balances) return [];

    return groupMemberService.sortGroupMembers(
      group.balances,
      group.createdBy,
      user?.id
    );
  }, [group?.balances, group?.createdBy, user?.id]);

  return {
    // Data
    group,
    sortedBalances,
    pendingInvites,
    excludeIds,

    // Loading states
    initialLoading,
    inviting,
    removing,
    promoting,
    renamingPlaceholder,
    removingPlaceholder,

    // Status
    error,
    success,
    setError,
    setSuccess,
    placeholderError,
    isOnline,

    // Computed
    isGroupOwner,
    isCurrentUserAdmin,

    // Actions
    handleRemoveMember,
    handlePromoteToAdmin,
    handleDemoteToMember,
    handleRenamePlaceholder,
    handleRemovePlaceholder,
    fetchMemberData,

    // User context
    user,
  };
}
